import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../../../lib/api-client';
import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

import type { SupervisionMessage, SupervisionWatcher } from './types';
import type { WsEventEnvelope } from '../../../lib/websocket/events';

export const supervisionKey = (conversationId: string): readonly string[] => [
  'conversation-supervision',
  conversationId,
];

/**
 * Quem acompanha esta conversa (só papéis visíveis ao operador) + histórico de coaching
 * visível. Reage em tempo real aos 4 eventos WS do co-atendimento — todos dirigidos por
 * `conversationId` no payload; evento de outra conversa é ignorado.
 *
 * 403 (atendente que não é o dono) vira `isError` e a UI simplesmente não mostra nada —
 * a mesma régua do GET da conversa.
 */
export function useConversationSupervision(conversationId: string): {
  watchers: SupervisionWatcher[];
  messages: SupervisionMessage[];
  isLoading: boolean;
  isError: boolean;
} {
  const qc = useQueryClient();
  const { subscribe } = useWebSocket();
  const q = useQuery({
    queryKey: supervisionKey(conversationId),
    queryFn: () => api.getConversationSupervision(conversationId),
    staleTime: 15_000,
  });

  useEffect(() => {
    const invalidate = (envelope: WsEventEnvelope<{ conversationId?: string }>): void => {
      const target = envelope.data.conversationId;
      if (target !== undefined && target !== conversationId) return;
      void qc.invalidateQueries({ queryKey: supervisionKey(conversationId) });
    };
    const offs = [
      subscribe(WS_EVENTS.COACHING_MESSAGE_CREATED, invalidate),
      subscribe(WS_EVENTS.COATTENDANCE_STARTED, invalidate),
      subscribe(WS_EVENTS.COATTENDANCE_ENDED, invalidate),
      subscribe(WS_EVENTS.COATTENDANCE_ROLE_CHANGED, invalidate),
    ];
    return () => {
      for (const off of offs) off();
    };
  }, [conversationId, qc, subscribe]);

  return {
    watchers: q.data?.watchers ?? [],
    messages: q.data?.messages ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
